import React from 'react';
import { Button, Stack } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type UserSetting } from '@soulhx/fs-common';
import { SHXEditor } from '../common/SHXEDitor.js';
import { useBulkUserSettings } from '@soulhx/client-store';
import { SHXCol } from '@soulhx/ui-common';

export const useTutorialMessages = (): {
  showUI: boolean;
  wizardSettings: UserSetting[];
  bulkUpdateFunc: (settings: UserSetting[]) => void;
} => {
  const { settings, settingsLoading, settingsError, bulkUpdate: bulkUpdateFunc } = useBulkUserSettings();

  if (settingsLoading || !!settingsError) return { showUI: false, wizardSettings: [], bulkUpdateFunc };

  const wizardSettings = settings.filter((setting) => setting.settingId.startsWith('wizard'));

  if (wizardSettings.every((setting) => setting.settingBoolValue === true)) {
    return { showUI: false, wizardSettings, bulkUpdateFunc };
  }

  return { showUI: true, wizardSettings, bulkUpdateFunc };
};

interface IDelayBtn {
  numDays: number;
  setTheDelayDate: (newDate: DateTime) => void;
  displayMessage: string;
}
const DelayBtn = ({ numDays, setTheDelayDate, displayMessage }: IDelayBtn) => {
  return (
    <Button variant="secondary" onClick={() => setTheDelayDate(DateTime.now().plus({ days: numDays }))}>
      {displayMessage}
    </Button>
  );
};

interface ISkipBtn {
  wizardSettings: UserSetting[];
  bulkUpdate: (update: UserSetting[]) => void;
  message: string;
}
const SkipBtn = ({ wizardSettings, bulkUpdate, message }: ISkipBtn) => {
  return (
    <Button
      variant="warning"
      onClick={() => {
        const updates: UserSetting[] = wizardSettings.map((s) => ({
          settingId: s.settingId,
          settingBoolValue: true,
        }));
        bulkUpdate(updates);
      }}
    >
      {message}
    </Button>
  );
};

export const TutorialMessages = () => {
  const { t } = useTranslation();
  const { showUI, wizardSettings, bulkUpdateFunc: bulkUpdate } = useTutorialMessages();

  if (!showUI) return null;

  const setTheDelayDate = (newDate: DateTime) => {
    const dateString = newDate.toISODate({})!;

    bulkUpdate([{ settingId: 'generalWizardDelayDate', settingStringValue: dateString }]);
  };

  const delayDateString =
    wizardSettings.find((s) => s.settingId === 'generalWizardDelayDate')?.settingStringValue || '';
  const delayDate = DateTime.fromISO(delayDateString);
  const pastOrNoDate = !delayDate.isValid || delayDate.diffNow().milliseconds < 0;

  if (!pastOrNoDate) return null;

  return (
    <SHXCol grow={1} minWidth="400px">
      <h5 className="text-center">{t('messages.tutorial.inline.header')}</h5>

      <SHXEditor initialMarkdown={t('messages.tutorial.inline.body')} readOnly className="mb-3" />

      <Stack gap={2} direction="vertical">
        <NavLink
          to="/tutorial"
          className="btn btn-primary"
          onClick={() => setTheDelayDate(DateTime.now().plus({ days: 1 }))}
        >
          {t('messages.tutorial.inline.nowBtn')}
        </NavLink>

        <DelayBtn
          numDays={3}
          setTheDelayDate={setTheDelayDate}
          displayMessage={t('messages.tutorial.delayBtn', { count: 3 })}
        />

        <DelayBtn
          numDays={5}
          setTheDelayDate={setTheDelayDate}
          displayMessage={t('messages.tutorial.delayBtn', { count: 5 })}
        />

        <SkipBtn wizardSettings={wizardSettings} bulkUpdate={bulkUpdate} message={t('messages.tutorial.skipBtn')} />
      </Stack>
    </SHXCol>
  );
};
